import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapCurrentUser, type UserProfile } from '@/api/mappers';

/**
 * Fetches the authenticated user profile from `/me` and returns the mapped
 * view model. Server state is the source of truth for permissions — the
 * auth provider only owns the token + identity stub.
 */
export function useMe(): UseQueryResult<UserProfile> {
  return useQuery<UserProfile>({
    queryKey: ['me'],
    queryFn: async ({ signal }) => {
      const dto = await apiClient.getCurrentUser({ signal });
      return mapCurrentUser(dto);
    },
    staleTime: 5 * 60_000,
    // /me is the auth probe. On 401 the handler navigates immediately; on
    // any other failure (CORS / network / 5xx) retrying just multiplies the
    // noise without changing the outcome. Bail on the first attempt.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
